import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  InterviewQuestionProps,
  IQuestion,
} from "./CandidateInterviewQuestion.model";
import { candidateExamService } from "../../service/CandidateExam/candidateExam.service";
import { QuestionExamTimerBox } from "./QuestionExamTimerBox";
import WritingSpeedTest from "../../components/WritingSpeedTest";

export default function CandidateInterviewQuestion({
  interviewQuestions,
  exam_id: examId,
}: InterviewQuestionProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [answerSubmitted, setAnswerSubmitted] = useState(false);

  const hasWarnedRef = useRef(false);
  const tabSwitchCountRef = useRef(0);
  const isSubmittingRef = useRef(false);

  const isWritingTest = currentIndex === 0;
  const mcqQuestions = interviewQuestions;
  const currentQuestion = mcqQuestions[currentIndex - 1];
  const writingTestCompleted = !!answers.writing_test;

  const formatWritingTest = (wt: any) =>
    wt
      ? [
          {
            accuracy: wt.accuracy,
            speed: `${wt.speedPercentage.toFixed(2)}%`,
            wpm: wt.wpm,
            cpm: wt.cpm,
            startTime:
              wt.startTime && new Date(wt.startTime).toLocaleTimeString(),
            endTime: wt.endTime && new Date(wt.endTime).toLocaleTimeString(),
          },
        ]
      : [];

  const submitExam = async () => {
    if (answerSubmitted) return;
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;

    const questionAnswers = Object.entries(answers);
    const formatted = questionAnswers
      .filter(([key]) => key !== "writing_test")
      .map(([qId, cId]) => ({
        question_id: Number(qId),
        selected_choice_id: Number(cId),
      }));

    const payload = {
      answers: formatted,
      writing_test: formatWritingTest(answers.writing_test),
    };

    try {
      await candidateExamService.submitExam(examId, payload);
      setAnswerSubmitted(true);
    } catch (error) {
      console.error("Submit error:", error);
      isSubmittingRef.current = false; // allow retry if needed
    }
  };

  const handleWritingAccuracy = async (data: any) => {
    setAnswers((prev) => ({ ...prev, writing_test: data }));
    try {
      await candidateExamService.saveAnswer(examId, {
        writing_test: formatWritingTest(data),
        question_id: 0,
        selected_choice_id: 0,
      });
    } catch (error) {
      console.error("Save writing test error:", error);
    }
  };

  const handleAnswer = (questionId: number, choiceId: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: choiceId,
    }));
  };

  // ✅ BLOCK F12, DEVTOOLS SHORTCUTS, RIGHT-CLICK
  useEffect(() => {
    if (answerSubmitted) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(e.key.toUpperCase())) ||
        (e.ctrlKey && e.key.toUpperCase() === "U")
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [answerSubmitted]);

  // ✅ DETECT DEVTOOLS OPEN (window size heuristic)
  useEffect(() => {
    if (answerSubmitted) return;

    let devToolsOpen = false;

    const checkDevTools = () => {
      const threshold = 160;
      const isOpen =
        window.outerWidth - window.innerWidth > threshold ||
        window.outerHeight - window.innerHeight > threshold;

      if (isOpen && !devToolsOpen) {
        devToolsOpen = true;
        confirmDialog({
          header: "Warning",
          message:
            "Developer tools are not allowed during the exam. Please close them to continue.",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "OK",
          rejectClassName: "hidden",
          rejectLabel: "",
          accept: () => {
            devToolsOpen = false;
          },
        });
      } else if (!isOpen) {
        devToolsOpen = false;
      }
    };

    const interval = setInterval(checkDevTools, 1000);
    return () => clearInterval(interval);
  }, [answerSubmitted]);

  // ✅ TAB CHANGE RULE:
  // 1st time -> show warning
  // 2nd time -> auto submit immediately

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (answerSubmitted) return;

      if (document.visibilityState === "hidden") {
        tabSwitchCountRef.current += 1;

        // Second time: auto-submit immediately
        if (tabSwitchCountRef.current >= 2) {
          // Optional: show a quick info dialog (non-blocking)
          confirmDialog({
            header: "Exam Submitted",
            message:
              "You switched tabs again. Your exam is being submitted automatically.",
            icon: "pi pi-exclamation-triangle",
            rejectClassName: "hidden",
            rejectLabel: "",
            acceptLabel: "OK",
          });

          await submitExam();
          return;
        }

        // First time: warning only (avoid spamming)
        if (hasWarnedRef.current) return;
        hasWarnedRef.current = true;

        confirmDialog({
          header: "Warning",
          message:
            "You switched tabs. Next time your exam will be submitted automatically.",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "OK",
          rejectClassName: "hidden",
          rejectLabel: "",
          accept: () => {
            // allow showing warning again later if you want (not required)
            setTimeout(() => {
              hasWarnedRef.current = false;
            }, 800);
          },
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [answerSubmitted, currentIndex, currentQuestion, answers, examId]);

  // ✅ REFRESH / CLOSE WARNING (native browser dialog only)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (answerSubmitted) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [answerSubmitted]);

  return (
    <>
      <ConfirmDialog />

      {answerSubmitted ? (
        <div className="flex flex-column justify-content-center align-items-center font-bold">
          <p>Your exam is submitted</p>
          <p>Thank you for your participation!</p>
        </div>
      ) : (
        <div className="candidate-exam-layout flex h-screen w-full">
          <div className="candidate-exam-nav p-3 border-right-1 surface-border overflow-auto">
            <div
              className={`p-2 border-round cursor-pointer mb-2 transition-all
                ${currentIndex === 0 ? "text-blue-600" : ""}
                ${writingTestCompleted ? "text-green-600" : "text-red-600"}`}
              onClick={() => setCurrentIndex(0)}>
              Writing Test
            </div>

            {mcqQuestions.map((q: IQuestion, i) => {
              const isActive = currentIndex === i + 1;
              const isAnswered = answers[q.id];
              return (
                <div
                  key={q.id}
                  className={`p-2 border-round cursor-pointer mb-2 transition-all
                ${isActive ? "text-blue-600 bg-primary-100" : ""}
                ${!isActive && isAnswered ? "text-green-600" : ""}
                ${!isActive && !isAnswered ? "text-red-600" : ""}
                ${
                  !writingTestCompleted ? "opacity-50 pointer-events-none" : ""
                }`}
                  onClick={() => {
                    if (writingTestCompleted) setCurrentIndex(i + 1);
                  }}>
                  Question {i + 1}
                </div>
              );
            })}
          </div>

          {/* CENTER CONTENT */}
          <div className="candidate-exam-content flex-auto p-2 flex justify-content-center overflow-auto">
            <div className="candidate-exam-card surface-card p-4 border-round shadow-2 w-full">
              {isWritingTest ? (
                <>
                  <h2 className="text-2xl mb-3 font-semibold">
                    Writing Speed Test
                  </h2>
                  <WritingSpeedTest
                    onFinish={handleWritingAccuracy}
                    answers={answers.writing_test}
                  />
                </>
              ) : (
                <>
                  <div className="flex flex-column sm:flex-row sm:justify-content-between gap-2">
                    <p>Question {currentIndex}</p>
                    <p className="font-bold">Topic: {currentQuestion?.topic}</p>
                  </div>

                  <pre
                    className="text-xl mb-4 font-semibold w-full"
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}>
                    {currentQuestion?.question_text}
                  </pre>

                  <div className="flex flex-column gap-3">
                    {currentQuestion?.choices.map((choice) => (
                      <div key={choice.id} className="flex align-items-center">
                        <RadioButton
                          inputId={`${choice.id}`}
                          value={choice.id}
                          checked={answers[currentQuestion.id] === choice.id}
                          onChange={() =>
                            handleAnswer(currentQuestion.id, choice.id)
                          }
                        />
                        <label htmlFor={`${choice.id}`} className="ml-2">
                          {choice.choice_text}
                        </label>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-column sm:flex-row justify-content-between gap-2 mt-4">
                    <Button
                      label="Previous"
                      disabled={currentIndex === 0}
                      onClick={() => setCurrentIndex((prev) => prev - 1)}
                    />

                    {currentIndex !== mcqQuestions.length && (
                      <Button
                        label="Next"
                        disabled={!writingTestCompleted}
                        onClick={async () => {
                          if (!writingTestCompleted) return;
                          const questionId = currentQuestion?.id;
                          const selectedChoiceId = answers[questionId];

                          if (questionId && selectedChoiceId !== undefined) {
                            try {
                              await candidateExamService.saveAnswer(examId, {
                                question_id: questionId,
                                selected_choice_id: selectedChoiceId,
                              });
                            } catch (error) {
                              console.error("Save answer error:", error);
                            }
                          }

                          setCurrentIndex((prev) => prev + 1);
                        }}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* RIGHT SIDE TIMER */}
          <div className="candidate-exam-aside p-3 border-left-1 surface-border flex flex-column align-items-center overflow-auto">
            <QuestionExamTimerBox
              onAutoSubmit={submitExam}
              totalQuestions={mcqQuestions?.length + 1}
            />

            <div className="flex flex-wrap gap-2 justify-content-center">
              <div
                className={`w-3rem h-3rem border-circle flex align-items-center 
                justify-content-center text-white cursor-pointer
                ${writingTestCompleted ? "bg-green-500" : "bg-red-500"}`}
                onClick={() => setCurrentIndex(0)}>
                WT
              </div>
              {mcqQuestions.map((q: IQuestion, i) => {
                const isActive = currentIndex === i + 1;
                const isAnswered = answers[q.id];
                return (
                  <div
                    key={q.id}
                    className={`w-3rem h-3rem border-circle flex align-items-center 
                    justify-content-center text-white cursor-pointer
                    ${
                      isActive
                        ? "bg-primary-500"
                        : isAnswered
                          ? "bg-green-500"
                          : "bg-red-500"
                    }
                    ${
                      !writingTestCompleted
                        ? "opacity-50 pointer-events-none"
                        : ""
                    }`}
                    onClick={() => {
                      if (writingTestCompleted) setCurrentIndex(i + 1);
                    }}>
                    {i + 1}
                  </div>
                );
              })}
            </div>

            <Button
              label="Submit Exam"
              className="p-button-danger w-full mt-4"
              disabled={!writingTestCompleted}
              onClick={() =>
                confirmDialog({
                  header: "Submit Exam",
                  message: (
                    <span>
                      Are you sure you want to submit your exam?
                      <br />
                      This action is final — once submitted, you will not be
                      able to change any answers or return to this page.
                    </span>
                  ),
                  icon: "pi pi-exclamation-triangle",
                  acceptLabel: "Yes, Submit",
                  rejectLabel: "Cancel",
                  acceptClassName: "p-button-danger",
                  accept: submitExam,
                })
              }
            />
            <p className="text-sm text-red-500 text-center mt-2 mb-0">
              Note: Once you attend all questions, then click the Submit button.
            </p>
          </div>

          <style>{`
            .candidate-exam-layout {
              min-width: 0;
            }

            .candidate-exam-nav {
              flex: 0 0 15rem;
              max-width: 18rem;
            }

            .candidate-exam-content {
              min-width: 0;
            }

            .candidate-exam-card {
              max-width: 64rem;
              min-width: 0;
            }

            .candidate-exam-aside {
              flex: 0 0 18rem;
              max-width: 20rem;
              min-width: 15rem;
            }

            @media (max-width: 900px) {
              .candidate-exam-layout {
                flex-direction: column;
                height: auto !important;
                min-height: 100vh;
              }

              .candidate-exam-nav {
                flex: 0 0 auto;
                max-width: none;
                width: 100%;
                border-right: 0 !important;
                border-bottom: 1px solid var(--surface-border);
                display: flex;
                gap: 0.5rem;
                overflow-x: auto;
              }

              .candidate-exam-nav > div {
                flex: 0 0 auto;
                margin-bottom: 0 !important;
                white-space: nowrap;
              }

              .candidate-exam-content {
                overflow: visible !important;
              }

              .candidate-exam-card {
                padding: 1rem !important;
                max-width: none;
              }

              .candidate-exam-aside {
                flex: 0 0 auto;
                max-width: none;
                min-width: 0;
                width: 100%;
                border-left: 0 !important;
                border-top: 1px solid var(--surface-border);
                overflow: visible !important;
              }
            }

            @media (max-width: 480px) {
              .candidate-exam-nav,
              .candidate-exam-content,
              .candidate-exam-aside {
                padding: 0.75rem !important;
              }

              .candidate-exam-aside .p-button {
                min-height: 2.75rem;
              }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
